import { Variants } from "framer-motion";
// anime.js'yi dinamik import ile kullanma
// import anime from "animejs";

// -------------------------------
// FRAMER MOTION SAYFA ANIMASYONLARI
// -------------------------------

// Standart sayfa geçiş animasyonu - daha yumuşak
export const pageVariants: Variants = {
  initial: { opacity: 0, y: 15 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1], // cubic-bezier
    }
  },
  exit: { 
    opacity: 0, 
    y: -15,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.1, 0.25, 1],
    } 
  },
};

// Fade in animasyonu (öğeler için)
export const fadeInAnimationVariants: Variants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  animate: (index: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.07 * index, // Biraz daha belirgin stagger
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

// Yavaş ve yumuşak geçiş (hakkımızda sayfası gibi içerik ağırlıklı sayfalar için)
export const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: {
      duration: 0.7,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0,
    transition: {
      duration: 0.3
    }
  },
};

// Ürün sayfaları için 3D efektli geçiş animasyonu 
export const productPageVariants: Variants = {
  initial: { 
    opacity: 0, 
    scale: 0.98,
    y: 10,
    rotateX: 2 // Hafif 3D efekti
  },
  animate: { 
    opacity: 1, 
    scale: 1,
    y: 0,
    rotateX: 0,
    transition: {
      duration: 0.6,
      ease: [0.215, 0.61, 0.355, 1] // easeOutCubic
    }
  },
  exit: { 
    opacity: 0, 
    scale: 1.02,
    transition: {
      duration: 0.4
    }
  },
};

// Katalog sayfası için özel animasyon - daha yumuşak
export const catalogPageVariants: Variants = {
  initial: { 
    opacity: 0, 
    x: -15,
    scale: 0.99
  },
  animate: { 
    opacity: 1, 
    x: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
  exit: { 
    opacity: 0, 
    x: 15,
    transition: {
      duration: 0.3
    }
  },
};

// Yeni - Ürün kartları için hover animasyonu
export const productCardVariants: Variants = {
  initial: { scale: 1 },
  hover: { 
    scale: 1.03, 
    y: -5,
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
    transition: {
      duration: 0.2,
      ease: "easeOut"
    }
  },
};

// Yeni - Modal açılış animasyonu
export const modalVariants: Variants = {
  initial: { 
    opacity: 0, 
    scale: 0.9, 
    y: 20 
  },
  animate: { 
    opacity: 1, 
    scale: 1, 
    y: 0,
    transition: {
      type: "spring",
      damping: 20,
      stiffness: 300
    }
  },
  exit: { 
    opacity: 0, 
    scale: 0.95,
    transition: {
      duration: 0.2
    }
  }
};

// Geçiş süreleri
export const defaultTransition = {
  duration: 0.35,
  ease: [0.25, 0.1, 0.25, 1], // cubic-bezier
};

export const slowTransition = {
  duration: 0.6,
  ease: [0.215, 0.61, 0.355, 1], // easeOutCubic
};

// Staggered animasyon için container (birden fazla elemanın sırayla animasyonu için)
export const staggerContainer: Variants = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

// Stagger için item varyantı
export const itemVariants: Variants = {
  initial: { y: 20, opacity: 0 },
  animate: { 
    y: 0, 
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  },
};

// Yeni - Kenarlarda görünüp kaybolma animasyonu
export const slideInOutVariants: Variants = {
  initial: { 
    x: 30, 
    opacity: 0 
  },
  animate: { 
    x: 0, 
    opacity: 1,
    transition: {
      duration: 0.5
    }
  },
  exit: { 
    x: -30, 
    opacity: 0,
    transition: {
      duration: 0.3
    }
  }
};

// Yeni - İçerik yükleme skeleton animasyonu
export const skeletonVariants: Variants = {
  initial: {
    opacity: 0.6,
  },
  animate: {
    opacity: 1,
    transition: {
      repeat: Infinity,
      repeatType: "reverse",
      duration: 0.8,
    },
  },
};

// -------------------------------
// ANIME.JS ANIMASYONLARI
// -------------------------------

// Dinamik olarak anime.js'yi import et
const getAnime = async () => {
  const animeModule = await import('animejs');
  return animeModule.default;
};

// Viewport'a girdiğinde element animasyonu
export const animateWhenVisible = async (element: string, options = {}) => {
  const anime = await getAnime();
  const defaultOptions = {
    opacity: [0, 1],
    translateY: [40, 0],
    duration: 900,
    easing: "easeOutQuad",
    delay: anime.stagger(150),
  };

  return anime({
    targets: element,
    ...Object.assign(defaultOptions, options),
  });
};

// Yukarıdan aşağı kayma animasyonu - daha akıcı
export const slideFromTop = async (element: string, delay = 0) => {
  const anime = await getAnime();
  return anime({
    targets: element,
    translateY: ["-100%", "0%"],
    opacity: [0, 1],
    duration: 800,
    easing: "easeOutQuad",
    delay: delay,
  });
};

// Soldan sağa kayma animasyonu
export const slideFromLeft = async (element: string, delay = 0) => {
  const anime = await getAnime();
  return anime({
    targets: element,
    translateX: ["-100%", "0%"],
    opacity: [0, 1],
    duration: 800,
    easing: "easeOutQuad",
    delay: delay,
  });
};

// Sağdan sola kayma animasyonu
export const slideFromRight = async (element: string, delay = 0) => {
  const anime = await getAnime();
  return anime({
    targets: element,
    translateX: ["100%", "0%"],
    opacity: [0, 1],
    duration: 800,
    easing: "easeOutQuad",
    delay: delay,
  });
};

// Pulse (titreşim) animasyonu
export const pulseAnimation = async (element: string, scale = [1, 1.03]) => {
  const anime = await getAnime();
  return anime({
    targets: element,
    scale: scale,
    duration: 600,
    direction: "alternate",
    easing: "easeInOutSine",
    loop: true
  });
};

// Çoklu element staggered animasyon
export const staggerAnimation = async (elements: string, options = {}) => {
  const anime = await getAnime();
  const defaultOptions = {
    opacity: [0, 1],
    translateY: [20, 0],
    duration: 800,
    easing: "easeOutQuad",
    delay: anime.stagger(100)
  };

  return anime({
    targets: elements,
    ...Object.assign(defaultOptions, options),
  });
};

// Dikkat çekme animasyonu
export const attentionAnimation = async (element: string) => {
  const anime = await getAnime();
  return anime({
    targets: element,
    scale: [1, 1.1, 1],
    duration: 800,
    easing: "easeInOutQuad"
  });
};

// Rakam sayma animasyonu (0'dan hedef değere doğru artan)
export const countAnimation = async (element: string, target: number, duration = 2000) => {
  const anime = await getAnime();
  return anime({
    targets: element,
    textContent: [0, target],
    round: 1,
    easing: 'easeInOutExpo',
    duration: duration
  });
};
